import {
  SIMULATED_ROLE_HEADER,
  parseSimulatedRole,
  type SimulatableRole,
} from "~/server/api/utils/roles"
import { isProduction } from "~/utils/environment"

/** sessionStorage key holding the simulated role. Written by `userProvider`'s
 * `useSessionStorage` (usehooks-ts), which JSON-serializes — the stored value
 * is e.g. `"read_only"` WITH quotes. Per-tab by design: a new tab always
 * starts un-simulated, which doubles as the lockout escape hatch. */
export const SIMULATED_ROLE_STORAGE_KEY = "hakio-override-role"

/** The stored value and whether simulation is permitted at all → the simulated role, or `null`
 * for every other case: not permitted, no value, a value that isn't JSON, or one that isn't a
 * customer tier. The testable core of `readSimulatedRole`, free of the browser globals. */
export function resolveStoredRole(
  raw: string | null,
  simulationAllowed: boolean,
): SimulatableRole | null {
  if (!simulationAllowed || !raw) return null
  try {
    return parseSimulatedRole(JSON.parse(raw))
  } catch {
    return null
  }
}

/** The currently simulated customer role, or `null` when not simulating — including when there
 * is no sessionStorage to read at all. Every failure mode falls back to the real role, the safe
 * direction; production is one of them, simulation being a developer affordance only. */
export function readSimulatedRole(): SimulatableRole | null {
  try {
    return resolveStoredRole(sessionStorage.getItem(SIMULATED_ROLE_STORAGE_KEY), !isProduction())
  } catch {
    return null
  }
}

/** Header fragment announcing the simulated role to the server; spread into
 * outgoing tRPC headers / SSE connectionParams. Empty when not simulating, so
 * requests are byte-identical to today outside simulation. The server ignores
 * the header unless the real JWT is staff (see `resolveEffectiveRoles`). */
export function simulatedRoleHeader(): Record<string, string> {
  const role = readSimulatedRole()
  return role ? { [SIMULATED_ROLE_HEADER]: role } : {}
}
